import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./styles/SuccessStories.scss";
import ErrorBoundary from './ErrorBoundary';
import heroVideo from "../assets/images/del_couch_everyday_hero_story (1080p).mp4";
import heroPoster from "../assets/images/Del.jpeg";
import { fetchStories, storyAnchorId, initialsOf } from "../lib/stories";

export default function SuccessStories() {
  const [stories, setStories] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    fetchStories().then((merged) => {
      setStories(merged);
      const initialIndices = {};
      merged.forEach((story) => {
        initialIndices[story._id] = 0;
      });
      setCurrentImageIndex(initialIndices);
      setLoading(false);
    });
  }, []);

  // After stories render, scroll to the card referenced by the URL hash
  // (e.g. arriving from a Home-page card link: /success-stories#story-<id>).
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("story-item--highlight");
      setTimeout(() => el.classList.remove("story-item--highlight"), 2200);
    }, 250);
    return () => clearTimeout(t);
  }, [loading]);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleNextImage = (storyId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [storyId]: (prev[storyId] + 1) % imagesLength
    }));
  };

  const handlePrevImage = (storyId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [storyId]: (prev[storyId] - 1 + imagesLength) % imagesLength
    }));
  };

  const handlePlayVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  return (
    <ErrorBoundary>
      <div className="success-stories">
        {/* Hero Video Section */}
        <section className="hero-video-section">
          <div className="hero-video-container">
            <video
              ref={videoRef}
              className="hero-video"
              preload="none"
              poster={heroPoster}
              controls={isPlaying}
              onPause={handleVideoPause}
              onPlay={handleVideoPlay}
              onEnded={handleVideoPause}
            >
              <source src={heroVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {!isPlaying && (
              <motion.div 
                className="hero-video-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="hero-video-content">
                  <motion.span 
                    className="hero-video-badge"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    Featured Story
                  </motion.span>
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Del Couch: Everyday Hero
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    Discover how the Del Couch Music Education Foundation is transforming lives through the power of music.
                  </motion.p>
                  <motion.button
                    className="hero-play-button"
                    onClick={handlePlayVideo}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="play-icon">▶</span>
                    Watch the Story
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>
        </section>

        <section className="header-section">
          <h1>Our Impact</h1>
          <p>Celebrating the achievements of our talented alumni</p>
        </section>

        <section className="stories-section">
          {loading ? (
            <p>Loading success stories...</p>
          ) : stories.length === 0 ? (
            <p>No success stories found. Please add some through the Sanity studio.</p>
          ) : (
            stories.map((story, index) => {
              return (
                <motion.div
                  key={story._id}
                  id={storyAnchorId(story._id)}
                  className={`story-item ${index % 2 === 0 ? "left" : "right"}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }} // Reduced threshold
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  <h2>{story.name}</h2>
                  <div className="image-carousel">
                    {story.images && story.images.length > 0 ? (
                      <>
                        {story.images.length > 1 && (
                          <button 
                            className="carousel-button left" 
                            onClick={() => handlePrevImage(story._id, story.images.length)}
                          >
                            ❮
                          </button>
                        )}
                        <img
                          src={story.images[currentImageIndex[story._id] ?? 0]?.url}
                          alt={story.name}
                          onClick={() => handleImageClick(story.images[currentImageIndex[story._id] ?? 0]?.url)}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/fallback-image.jpg';
                          }}
                        />
                        {story.images.length > 1 && (
                          <button 
                            className="carousel-button right" 
                            onClick={() => handleNextImage(story._id, story.images.length)}
                          >
                            ❯
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="story-initials" aria-hidden="true">
                        <span>{initialsOf(story.name)}</span>
                      </div>
                    )}
                    {story.images?.[currentImageIndex[story._id] ?? 0]?.credit && (
                      <p className="story-credit">
                        {story.images[currentImageIndex[story._id] ?? 0].credit}
                      </p>
                    )}
                  </div>

                  <div className="content">
                    <p className="description">{story.description}</p>
                    {story.achievements && story.achievements.length > 0 && (
                      <div className="achievements">
                        <h3>Achievements</h3>
                        <ul>
                          {story.achievements.map((achievement, i) => (
                            <li key={i}>{achievement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {story.mediaLinks && story.mediaLinks.length > 0 && (
                      <div className="media-links">
                        <h3>Related Media</h3>
                        <div className="links-grid">
                          {story.mediaLinks.map((link, i) => (
                            <a
                              key={i}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`media-link ${link.type}`}
                            >
                              {link.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
        </section>

        {selectedImage && (
          <div className="lightbox" onClick={() => setSelectedImage(null)}>
            <img src={selectedImage} alt="Full size" />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
} 